import gql from 'graphql-tag';

const insurtechBasicFragments = {
    Heading: gql`
        fragment InsurtechModuleHeadingFragment on InsurtechModuleHeading {
            heading
            subHeading
            graphic {
                ...GraphicIllustrationFragment
            }
            additionalInfo {
                contents {
                    __typename
                    ... on EGDSPlainText {
                        text
                        accessibility
                    }
                    ... on InsurTechExperienceDialogTrigger {
                        primary
                        dialogId
                        accessibility
                        analytics {
                            ...ClientSideAnalyticsFragment
                        }
                    }
                    ... on InsurtechPopover {
                        ...InsurtechPopoverFragment
                    }
                }
            }
        }
    `,
    FOOTER: gql`
        fragment InsurtechShoppingModuleFooterFragment on InsurtechShoppingModuleFooter {
            __typename
            ... on InsurtechShoppingModuleTextFooter {
                content {
                    ...InsurTechTextWrapperElementFragment
                }
            }
        }
    `,
    UpdateProductSelectionAction: gql`
        fragment InsurTechUpdateProductSelectionActionFragment on InsurtechUpdateProductSelectionAction {
            productSku
            productId
            responseId
            addInsuranceErrorMessage
            removeInsuranceErrorMessage
            accessibility
            analytics {
                ...ClientSideAnalyticsFragment
            }
        }
    `,
    UrgencyMessageFragment: gql`
        fragment InsurtechUrgencySectionFragment on InsurtechUrgencySection {
            viewType
            graphic {
                ... on Icon {
                    id
                    size
                }
            }
            message {
                text
            }
        }
    `,
    BenefitSection: gql`
        fragment InsurTechBenefitSectionFragment on InsurtechBenefitSection {
            heading
            list {
                type
                accessibility
                items {
                    __typename
                    label
                    content {
                        __typename
                        ... on EGDSGraphicText {
                            text
                            graphic {
                                __typename
                                ...GraphicIllustrationFragment
                                ...IconFragment
                            }
                        }
                        ... on EGDSTextStandardListItem {
                            text
                        }
                        ... on EGDSPlainText {
                            text
                        }
                    }
                    ... on InsurtechIconListItem {
                        icon {
                            ... on Icon {
                                id
                                size
                                description
                            }
                        }
                    }
                }
            }
            dialogTrigger {
                ...InsurTechExperienceDialogTriggerFragment
            }
        }
    `,
    ClickableElement: gql`
        fragment InsurtechClickableElementFragment on InsurtechClickableElement {
            __typename
            ... on InsurtechSecondaryButton {
                __typename
                action {
                    ...InsurTechUpdateProductSelectionActionFragment
                }
                button {
                    ... on UISecondaryButton {
                        primary
                        accessibility
                        icon {
                            ... on Icon {
                                id
                                size
                                description
                            }
                        }
                    }
                    __typename
                }
            }
        }
    `,
    SelectionSection: gql`
        fragment InsurTechSelectionSectionFragment on InsurtechSelectionSection {
            clickableElement {
                ...InsurtechClickableElementFragment
            }
            selectedClickableElement {
                ...InsurtechClickableElementFragment
            }
            selectionPrice: price {
                ... on InsurtechPricePresentation {
                    text
                    substring
                }
                __typename
            }
        }
    `,
    InsurtechMiscellaneousSection: gql`
        fragment InsurtechMiscellaneousSectionFragment on InsurtechMiscellaneousSection {
            heading
            list {
                type
                items {
                    label
                    content {
                        __typename
                        ... on EGDSGraphicText {
                            text
                            graphic {
                                ...GraphicIllustrationFragment
                            }
                        }
                    }
                }
            }
        }
    `,
    TextWrapperElement: gql`
        fragment InsurTechTextWrapperElementFragment on InsurtechTextWrapperElement {
            __typename
            ... on InsurtechStandardLinkWrapper {
                ...InsurtechStandardLinkWrapperFragment
            }
            ...InsurtechSpannableTextFragment
        }
    `,
    TextWrapper: gql`
        fragment InsurTechTextWrapperFragment on InsurtechTextWrapper {
            text {
                ...InsurTechTextWrapperElementFragment
            }
        }
    `,
    StandardLinkWrapper: gql`
        fragment InsurtechStandardLinkWrapperFragment on InsurtechStandardLinkWrapper {
            link {
                ...EGDSStandardLinkFragment
            }
        }
    `,
    EGDSStandardLink: gql`
        fragment EGDSStandardLinkFragment on EGDSStandardLink {
            text
            action {
                accessibility
                resource {
                    value
                }
                analytics {
                    ...ClientSideAnalyticsFragment
                }
            }
        }
    `,
    CheckBoxWrapper: gql`
        fragment InsurTechCheckBoxWrapperFragment on InsurtechCheckBoxWrapper {
            __typename
            action {
                __typename
                ...InsurTechUpdateProductSelectionActionFragment
            }
            checkBox {
                ... on EGDSBasicCheckBox {
                    label {
                        __typename
                        ...EGDSPlainTextFragment
                        ...EGDSHeadingFragment
                    }
                }
            }
            graphic {
                ...GraphicIllustrationFragment
            }
            price {
                lockupPrice
                priceSubtextFirst
            }
            checkedAccessibilityLabel
            uncheckedAccessibilityLabel
        }
    `,
    RadioGroupWrapper: gql`
        fragment InsurTechRadioGroupWrapperFragment on InsurtechRadioGroupWrapper {
            label
            targetOfferIdentity {
                type
                value
            }
            insurtechRadioGroup {
                radioGroup {
                    label
                    errorMessage
                    groupName
                }
                options {
                    __typename
                    ... on InsurtechPriceRadioButton {
                        action {
                            __typename
                            ...InsurTechUpdateProductSelectionActionFragment
                        }
                        item {
                            label
                            labelSuffix
                            value
                        }
                        price {
                            text
                            substring
                            totalPriceLabel
                        }
                    }
                    ... on InsurtechShoppingRadioButton {
                        action {
                            __typename
                            ...InsurTechUpdateProductSelectionActionFragment
                        }
                        item {
                            label
                            labelSuffix
                            value
                        }
                        price {
                            text
                            substring
                            totalPriceLabel
                        }
                        body {
                            __typename
                            content {
                                __typename
                                ...InsurTechBenefitSectionFragment
                                ...InsurTechTextWrapperFragment
                                ...InsurtechMiscellaneousSectionFragment
                            }
                        }
                    }
                }
            }
        }
    `,
    InsurtechPopover: gql`
        fragment InsurtechPopoverFragment on InsurtechPopover {
            popoverText: text
            accessibility
            analytics {
                ...ClientSideAnalyticsFragment
            }
            content {
                ... on InsurtechPopoverTextContent {
                    content
                    heading
                }
            }
        }
    `,
    Testimonial: gql`
        fragment InsurTechTestimonialFragment on InsurtechTestimonial {
            header {
                __typename
                ...EGDSPlainTextFragment
            }
            author
            message
            expando {
                collapsedLabel
                expandedLabel
                expanded
                collapseAnalytics {
                    ...ClientSideAnalyticsFragment
                }
                expandAnalytics {
                    ...ClientSideAnalyticsFragment
                }
            }
            graphic {
                __typename
                ...IconFragment
            }
            tooltip {
                ...InsurtechPopoverFragment
                icon {
                    ...IconFragment
                }
            }
        }
    `,
    SpannableText: gql`
        fragment InsurtechSpannableTextFragment on InsurtechSpannableText {
            contents {
                __typename
                ...EGDSPlainTextFragment
                ...EGDSStandardLinkFragment
                ...EGDSStylizedTextFragment
                ...InsurtechGraphicStylizedTextFragment
                ...InsurTechExperienceDialogTriggerFragment
            }
        }
    `,
    IllustrationFragment: gql`
        fragment GraphicIllustrationFragment on Illustration {
            url
            description
        }
    `,
    IconFragment: gql`
        fragment IconFragment on Icon {
            id
            size
            description
        }
    `,
    EGDSPlainText: gql`
        fragment EGDSPlainTextFragment on EGDSPlainText {
            text
            accessibility
        }
    `,
    EGDSHeadingText: gql`
        fragment EGDSHeadingFragment on EGDSHeading {
            text
            accessibility
        }
    `,
    EGDSStylizedText: gql`
        fragment EGDSStylizedTextFragment on EGDSStylizedText {
            text
            accessibility
            size
            weight
        }
    `,
    DialogTrigger: gql`
        fragment InsurTechExperienceDialogTriggerFragment on InsurTechExperienceDialogTrigger {
            primary
            dialogId
            accessibility
            analytics {
                ...ClientSideAnalyticsFragment
            }
        }
    `,
    GraphicStylizedText: gql`
        fragment InsurtechGraphicStylizedTextFragment on InsurtechGraphicStylizedText {
            grapic {
                ...GraphicIllustrationFragment
            }
            stylizedText {
                ...EGDSStylizedTextFragment
            }
        }
    `,
    ClientSideAnalytics: gql`
        fragment ClientSideAnalyticsFragment on ClientSideAnalytics {
            linkName
            referrerId
        }
    `,
};

export default insurtechBasicFragments;
